import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ROLE_LABELS, useAuth } from './auth'
import { GROUP_ORDER, SCREENS, visibleScreens, type Screen } from './routes'
import ChangePassword from './views/ChangePassword'
import Login from './views/Login'
import { Loading, Note } from './components/ui'
import { Workspace } from './components/Workspace'

//: Two letters for the avatar. A name is the one thing on this rail that is
//: not the same for everybody, and at a glance the initials are what people
//: actually read to check which account they are in.
function initials(name: string | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  const letters = words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0]
  return letters.toUpperCase()
}

function Sidebar() {
  const { me, can, logout } = useAuth()
  const screens = visibleScreens(can, me?.role)

  return (
    <aside className="sidebar">
      {/* Who you are signed in as stays put while the links scroll. On a long
          navigation it used to scroll away, and "which account am I in?" is
          the question this system is least able to afford being unsure of:
          every screen below means something different depending on it. */}
      <div className="sidebar-head">
        <div className="brand">
          <span className="mark" aria-hidden="true">
            FP
          </span>
          Finance Platform
        </div>
        <div className="whoami">
          <span className="avatar" aria-hidden="true">
            {initials(me?.display_name)}
          </span>
          <span className="who">
            <strong title={me?.display_name}>{me?.display_name}</strong>
            <span className="role">{me ? ROLE_LABELS[me.role] ?? me.role : ''}</span>
          </span>
        </div>
        <div className="account">
          {/* Not in the navigation groups below: those are the company's
              screens, gated by permission. This is your own account, and
              every role reaches it. */}
          <NavLink to="/me/password">Password</NavLink>
          <button onClick={() => void logout()}>Sign out</button>
        </div>
      </div>
      <div className="sidebar-links">
      {GROUP_ORDER.map((group) => {
        const inGroup = screens.filter((s) => s.group === group)
        // A section with nothing this role may reach does not render at all,
        // rather than rendering empty or rendering links that 403.
        if (inGroup.length === 0) return null
        return (
          <div key={group}>
            <div className="group">{group}</div>
            <nav>
              {inGroup.map((s) => (
                <NavLink key={s.path} to={s.path} end={s.path === '/'}>
                  {s.title}
                </NavLink>
              ))}
            </nav>
          </div>
        )
      })}
      </div>
    </aside>
  )
}

/** Renders a screen, or explains why it cannot. */
function Guard({ screen }: { screen: Screen }) {
  const { can, me } = useAuth()
  if (me && screen.notForRoles?.includes(me.role)) {
    return (
      <Note kind="info">
        This screen is not part of what your role does, so it is not in your
        navigation. Nothing is hidden from you: ask for the figures on{' '}
        <NavLink to="/books/reports">Financial statements</NavLink>.
      </Note>
    )
  }
  if (screen.permission && !can(screen.permission)) {
    return (
      <Note kind="warn">
        Your role does not have access to this screen. If you believe it should,
        ask an administrator — access is decided by role, not by request.
      </Note>
    )
  }
  return <>{screen.element}</>
}

function Shell() {
  const location = useLocation()
  const active =
    (location.pathname === '/me/password'
      ? { title: 'Your password', blurb: 'Nobody can change it for you' }
      : undefined) ??
    SCREENS.find((s) => s.path === location.pathname) ??
    SCREENS.find(
      (s) => s.path.includes(':') && location.pathname.startsWith(s.path.split('/:')[0]),
    )

  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        <div className="topbar">
          <div className="titles">
            <h1>{active?.title ?? 'Not found'}</h1>
            {active?.blurb && <span className="sub">{active.blurb}</span>}
          </div>
          <Workspace />
        </div>
        <div className="content">
          <Routes>
            {SCREENS.map((s) => (
              <Route key={s.path} path={s.path} element={<Guard screen={s} />} />
            ))}
            {/* Deliberately not a `Screen`: it belongs to no group, carries no
                permission, and must never be hidden from anybody. */}
            <Route path="/me/password" element={<ChangePassword />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  )
}

export default function App() {
  const { me, loading } = useAuth()
  if (loading) return <Loading what="Checking your session" />
  if (!me) return <Login />
  // Before the shell, not inside it. Every route behind this would 403, and a
  // navigation full of links that refuse is a worse explanation than one
  // screen that says what is wrong.
  if (me.must_change_password) return <ChangePassword forced />
  return <Shell />
}
